import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { useCheckoutForms } from "@shared/modules/checkout/hooks/useCheckoutForms";
import { usePaymentManager } from "@shared/modules/payments/contexts/PaymentManagerContext";
import { PurchaseOptsType } from "@shared/modules/payments/types";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Box from "@ui-kit/layout/Box/Box.web";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { FormProvider } from "react-hook-form";
import CheckoutBaseLayoutWithCart from "@app-contexts/public/shared/checkout/templates/CheckoutBaseLayoutWithCart/CheckoutBaseLayoutWithCart.web";
import { formatCheckoutFormsToBasketEndpointsPayload } from "@shared/modules/checkout/utils/format-checkout-forms";
import { isStorageAvailable } from "@shared/utils/storage/cache-storage";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";
import CheckoutSummary from "@app-contexts/public/shared/checkout/components/CheckoutSummary/CheckoutSummary.web";

import { PurchaserInfoSection } from "./components/PurchaserInfoSection/PurchaserInfoSection.web";
import { PaymentSection } from "./components/PaymentSection/PaymentSection.web";
import { ShippingAddressSection } from "./components/ShippingAddressSection/ShippingAddressSection.web";
import { PurchaseButton } from "./components/PurchaseButton.web";
import {
  useCheckoutValidation,
  VALIDATION_FIELDS,
} from "./hooks/useCheckoutValidation";

/**
 * Ref for triggering the purchase click from outside the component
 */
export interface PaymentRef {
  handlePurchaseClick: () => Promise<void>;
}

type PaymentProps = {
  publicBasket: ReturnType<typeof usePublicBasket>;
  allowRedirect?: boolean;
  onValidationChange: (isValid: boolean) => void;
  hideCheckoutButton?: boolean;
};

/**
 * Public Checkout Payment Page
 */
const Payment = forwardRef<PaymentRef, PaymentProps>(function Payment(
  props,
  ref
) {
  const {
    publicBasket,
    allowRedirect = true,
    onValidationChange,
    hideCheckoutButton = false,
  } = props;
  const { manager, handleSetPurchaseData } = usePaymentManager();
  const checkoutForms = useCheckoutForms();

  // Use React Hook Form for validation
  const {
    form,
    isValid: canCheckout,
    triggerValidation,
  } = useCheckoutValidation();

  const [isShippingSameAsBillingAddress, setIsShippingSameAsBillingAddress] =
    useState(true);

  // Used only for validation with the shipping addresss form
  const [billingAddress, setBillingAddress] = useState<{
    info_home_address_country: string;
  } | null>(null);

  const [paymentFailures, setPaymentFailures] = useState(0);

  const isShippingAddressRequired = useMemo(() => {
    return publicBasket.itemGroups.some(
      (group) => group.shipping_type === enums.SHIPPING_METHODS.DELIVERY.id
    );
  }, [publicBasket.itemGroups]);

  const { useRetrieve } = usePublicVenueService();
  const venueId = publicBasket.paymentVenue;
  const { data: venueData } = useRetrieve(venueId);

  // Some payment options shown to the user must be explicitly enabled by the venue
  const enabledPaymentOptions = useMemo(() => {
    return {
      [PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id]: true,
      [PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id]:
        venueData?.affirm_is_enabled || false,
    };
  }, [venueData?.affirm_is_enabled]);

  /**
   * Notify parent of validation changes
   */
  useEffect(() => {
    onValidationChange(canCheckout);
  }, [canCheckout, onValidationChange]);

  /**
   * Update shipping address requirement in form
   */
  useEffect(() => {
    form.setValue(
      VALIDATION_FIELDS.shippingAddressRequired,
      isShippingAddressRequired
    );
  }, [isShippingAddressRequired, form]);

  const getFormattedAddress = useCallback(
    (formKey: "billingAddressForm" | "shippingAddressForm") => {
      const form =
        formKey === "billingAddressForm"
          ? checkoutForms.billingAddressForm
          : checkoutForms.shippingAddressForm;

      return formatCheckoutFormsToBasketEndpointsPayload(form);
    },
    [checkoutForms.billingAddressForm, checkoutForms.shippingAddressForm]
  );

  const getNameOnCard = useCallback(() => {
    return checkoutForms.manualCardEntryForm.getValues(
      "manual-card-entry-name"
    ) as string;
  }, [checkoutForms.manualCardEntryForm]);

  const getPurchaseOpts = useCallback(() => {
    const purchaseOpts: PurchaseOptsType = {
      billing_address: null,
      shipping_address: null,
    };

    const nameOnCard = getNameOnCard();
    const billingAddress = getFormattedAddress("billingAddressForm");

    if (nameOnCard) {
      purchaseOpts.name_on_card = nameOnCard;
    }

    // Billing address is always required on Payment.web
    if (
      billingAddress &&
      Object.values(billingAddress).some((value) => Boolean(value))
    ) {
      purchaseOpts.billing_address = billingAddress;
    }

    // Set shipping address if required
    if (isShippingAddressRequired) {
      const shippingAddress = isShippingSameAsBillingAddress
        ? billingAddress
        : getFormattedAddress("shippingAddressForm");

      if (shippingAddress && Object.values(shippingAddress).some(Boolean)) {
        purchaseOpts.shipping_address = shippingAddress;
      }
    }

    return purchaseOpts;
  }, [
    isShippingSameAsBillingAddress,
    isShippingAddressRequired,
    getFormattedAddress,
  ]);

  const handlePurchaseClick = useCallback(async () => {
    // Trigger validation for all sections
    // This signals components to show inline error messages and toast notifications
    const isValid = await triggerValidation();

    if (!isValid) {
      // Both inline errors and toast notifications are displayed when validation fails
      return;
    }

    try {
      const purchaseOpts = getPurchaseOpts();

      // For group sales, disable ticket PDF attachments in confirmation email
      // Group sales distribute tickets via the distribution portal instead
      // Similar to box office's updateDeliveryMethodsOnBasket()
      if (publicBasket.isGroupSale) {
        await publicBasket.modifyBasket(
          { ticket_confirmation_send_tickets: false },
          { shouldPersist: false }
        );
      }

      try {
        const purchaseArtifacts = await manager.purchase(
          // Manual card entry and BNPLs like Affirm also funnel through this
          // (eg. Credit Card and Affirm payment checkboxes use the same button for this handler),
          // to which manual_card_entry is preferred but may be overridden by the orchestra.
          // See AffirmPaymentHandler for more info.
          PAYMENT_MANAGER_CONFIRMATION_STRATEGY.MANUAL_CARD_ENTRY,
          purchaseOpts
        );
        handleSetPurchaseData(purchaseArtifacts);
        // Remove exchange credit data from local storage if it exists
        if (isStorageAvailable("localStorage")) {
          localStorage.removeItem("exchangeCreditData");
        }
      } catch {
        setPaymentFailures((state) => state + 1);

        // Errors stemming from manager.purchase should already be handled internally by the PaymentManager.
        // See PaymentManager.purchase for details.
      }
    } catch (e) {
      // Record errors arising from outside of payments orchestration
      recordAppError(e);
      setPaymentFailures((state) => state + 1);
    }
  }, [
    manager,
    getPurchaseOpts,
    handleSetPurchaseData,
    triggerValidation,
    publicBasket,
  ]);

  // Expose the handlePurchaseClick method via ref
  useImperativeHandle(ref, () => ({
    handlePurchaseClick,
  }));

  const limitedShippingCountries = useMemo(() => {
    if (
      venueData?.allowed_shipping_countries &&
      venueData.allowed_shipping_countries.length > 0
    ) {
      return venueData.allowed_shipping_countries;
    }
    return null;
  }, [venueData?.allowed_shipping_countries]);

  /**
   * This callback is used to update the shipping address form checkbox.
   * If the billing country is not valid for shipping, we need to prompt
   * the user to enter a valid shipping address.
   */
  const onBillingAddressChange = useCallback(
    (billingAddress: {
      // TODO: type this properly
      info_home_address_country: string;
    }) => {
      const isCountryValidForShipping =
        limitedShippingCountries?.includes(
          billingAddress.info_home_address_country
        ) ?? true;

      setIsShippingSameAsBillingAddress(isCountryValidForShipping);
      setBillingAddress(billingAddress);
    },
    [limitedShippingCountries]
  );

  return (
    <FormProvider {...form}>
      <CheckoutBaseLayoutWithCart
        hideCartOnMobile
        rightColumn={
          <CheckoutSummary
            showTermsAndConditions
            callToActionButton={
              hideCheckoutButton ? null : (
                <PurchaseButton
                  onPurchaseClick={handlePurchaseClick}
                  canCheckout={canCheckout}
                  paymentFailures={paymentFailures}
                />
              )
            }
          />
        }
      >
        <Box
          data-testid="checkout-payment"
          flex={1}
          backgroundColor="bg"
          padding={{ base: "16px 20px", sm: "16px 24px" }}
        >
          <PurchaserInfoSection
            publicBasket={publicBasket}
            venueData={venueData}
          />

          <Box mt="xl" mb="xl">
            <Divider />
          </Box>

          <PaymentSection
            venue={venueData}
            publicBasket={publicBasket}
            allowRedirect={allowRedirect}
            checkoutForms={checkoutForms}
            enabledPaymentOptions={enabledPaymentOptions}
            onBillingAddressChange={onBillingAddressChange}
          />

          {isShippingAddressRequired && (
            <ShippingAddressSection
              checkoutForms={checkoutForms}
              onSameAsBillingAddressChange={(isSameAsBillingAddress) => {
                setIsShippingSameAsBillingAddress(isSameAsBillingAddress);
              }}
              allowedCountries={limitedShippingCountries}
              isShippingSameAsBillingAddress={isShippingSameAsBillingAddress}
              billingAddress={billingAddress}
            />
          )}
        </Box>

        {/* Checkout Summary - visible on mobile only */}
        <Box bg="bg" display={{ base: "block", sm: "none" }}>
          <CheckoutSummary
            showTermsAndConditions
            callToActionButton={
              hideCheckoutButton ? null : (
                <PurchaseButton
                  onPurchaseClick={handlePurchaseClick}
                  canCheckout={canCheckout}
                  paymentFailures={paymentFailures}
                />
              )
            }
            asDrawerOnMobile={false}
          />
        </Box>
      </CheckoutBaseLayoutWithCart>
    </FormProvider>
  );
});

export default Payment;
